import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { alpha, keyframes, styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { hugsItsLabel } from "_shared/_views/buttons/action-button-width";
import LoadingButton from "_shared/_views/loading/loading-button";
import {
  paneIsCompact,
  paneIsComfortable,
  workPaneContainment,
  workPaneFullBleed,
  workPaneGutters,
  workPaneHeaderGutters,
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

export const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.05);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.85;
  }
`;

export const Header = styled("section")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
  position: "sticky",
  top: "0px",
  zIndex: 10,
}));

export const TabsContainer = styled(Tabs)(({ theme }) => ({
  "&.MuiTabs-root": {
    overflow: "hidden",
    "& .MuiTabs-scroller": {
      [paneIsCompact(theme)]: {
        paddingTop: 0,
      },
      paddingTop: theme.spacing(0.5),
    },
    "& .MuiTabs-scrollButtons.Mui-disabled": {
      opacity: "0.3",
    },
  },
}));

export const Content = styled("section")(({ theme }) => ({
  alignItems: "center",
  display: "contents",
  flex: 1,
  flexDirection: "row",
  height: "100%",
  minHeight: "25rem",
  width: "100%",
  [paneIsCompact(theme)]: {
    alignItems: "stretch",
    display: "flex",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
}));

export const TabContent = styled("section")(({ theme }) => ({
  alignItems: "stretch",
  flexDirection: "column",
  display: "flex",
  flex: 1,
  margin: theme.spacing(2, 0),
  minHeight: 0,
  overflow: "visible",
  width: "100%",
  [paneIsCompact(theme)]: {
    flex: "1 1 auto",
    margin: theme.spacing(1, 0, 0),
  },
}));

export const ActionsContainer = styled("section")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  backgroundColor: theme.palette.background.default,
  bottom: "0px",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  paddingBottom: theme.spacing(2.5),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));

export const IssueTrackerFooter = styled("section")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  bottom: 0,
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
  marginTop: "auto",
  paddingTop: theme.spacing(1),
  position: "sticky",
  width: "100%",
  zIndex: 15,
  [paneIsComfortable(theme)]: {
    display: "none",
  },
}));

export const IssueTrackerHeader = styled("section")(({ theme }) => ({
  display: "flex",
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));

export const ProposalActions = styled("section")(({ theme }) => ({
  alignContent: "end",
  alignItems: "center",
  display: "grid",
  flex: "0 1 auto",
  gap: theme.spacing(1),
  /**
   * As many buttons per row as the pane can seat, and never a row with one button stranded below it.
   *
   * Two things have to be true for that. The track's maximum is `1fr` rather than a fixed width,
   * because `auto-fit` counts its repetitions from the maximum sizing function whenever that one is
   * definite — a fixed 22rem decided that a 926px pane held two buttons however small the minimum
   * was, and left the third on a row of its own with 570px of nothing beside it. And the repetition
   * is `auto-fill` rather than `auto-fit`, because `auto-fit` collapses the tracks it did not fill
   * and hands their width to the ones it did, which would stretch three buttons across a pane wide
   * enough for six. `auto-fill` leaves the empty tracks standing, so a button is the same width
   * whether its row is full or not.
   */
  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 16rem), 1fr))",
  justifyContent: "start",
  maxWidth: "100%",
  width: "100%",
  // Capping the wrapper a tooltip needs, not stretching it: a grid item stretches on its own, and a
  // `width` here would outrank anything a single class says, including the one action that hugs.
  "& > span": {
    maxWidth: "22rem",
  },
  [paneIsCompact(theme)]: {
    flex: 1,
    gap: theme.spacing(0.5),
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))",
    width: "100%",
    "& > span": {
      maxWidth: "none",
    },
  },
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    gridTemplateColumns: "1fr",
  },
}));

export const ReviewerBadge = styled(Badge)(() => ({
  marginLeft: "1rem",
}));

export const CommentButton = styled("section")(() => ({
  alignContent: "center",
  justifyContent: "flex-end",
}));

export const ButtonContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(2),
}));

export const CommentButtonLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.palette.action.disabled : undefined,
  fontSize: "0.6em",
}));

export const HighlightCommentButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ active }) => ({
  height: "40px",
  width: "40px",
  position: "relative",
  zIndex: 0,
  ...(active && {
    // Animate only decoration so the interactive hit target remains stable.
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(21, 101, 192, 0.05)",
      borderRadius: "50%",
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));

export const SaveButtonLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.palette.action.disabled : undefined,
  fontSize: "0.6em",
}));

export const SaveButton = styled(IconButton)(() => ({
  height: "40px",
  width: "40px",
  position: "relative",
  zIndex: 0,
}));

export const Fieldset = styled("span")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridColumnEnd: "span 2",
  gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
  width: "100%",
  [paneIsCompact(theme)]: {
    display: "contents",
    gridColumnEnd: "auto",
  },
}));

export const ProposalActionButton = styled(LoadingButton)(({ theme }) => ({
  maxWidth: "22rem",
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
    "& .MuiButton-icon": {
      flexShrink: 0,
    },
    "& .MuiButton-startIcon": {
      flexShrink: 0,
    },
    "& .MuiButton-endIcon": {
      flexShrink: 0,
    },
  },
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

/**
 * The way out of a negotiation, drawn to be found rather than pressed.
 *
 * Cancel Proposal and End Negotiation are one action seen from the two sides of a negotiation — the
 * person who opened it cancels, the person who received it ends — and both destroy work. Neither is
 * what a reader came to the page for, so neither takes a whole track of a row whose other buttons
 * carry the negotiation forward. Issue #5240 is the report that they did, and that Leave Team on a
 * work agreement is the shape they should have had.
 *
 * They take the last column of the row and sit against its right edge, which is Leave Team's shape
 * exactly. Hugging alone was not enough. A narrower button leaves whitespace after itself, and when
 * the way out is the last control in the row — which is what the vendor sees, where Reviewers and
 * Submit Approval come before End Negotiation — there is no button after it for that whitespace to
 * separate it from, so it stayed shoulder to shoulder with the control that carries the work on.
 * Distance from the others is the point, so it is stated rather than left to fall out of a width.
 *
 * The column is named from the end (`-2 / -1`) because the row's track count follows the pane, not
 * the button count. Grid places a definitely-positioned item before any auto-placed one, so this
 * one claims the last column first and the rest fill in from the left, whatever the pane is doing.
 *
 * Where the pane is too narrow for two tracks the row is a single stacked column, and a full-width
 * button is what reads correctly there — so the hug and the pinning are lifted together.
 * `grid-column` has to be lifted with them: in a one-track grid the last column is the only column,
 * and claiming it first would put the way out at the top of the stack rather than in its place.
 *
 * Both halves carry the placement because only one of the two call sites has a span. Cancel Proposal
 * is disabled while a save is in flight and so needs the wrapper its tooltip hangs on, and the
 * wrapper is then the grid item; End Negotiation has no tooltip and is the grid item itself.
 */
export const ProposalQuietActionSpan = styled("span")(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    gridColumn: "auto",
    justifySelf: "stretch",
  },
  gridColumn: "-2 / -1",
  justifySelf: "end",
}));

export const ProposalQuietActionButton = styled(ProposalActionButton)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    gridColumn: "auto",
    justifySelf: "stretch",
    minWidth: theme.spacing(24),
    width: "100%",
  },
  gridColumn: "-2 / -1",
  justifySelf: "end",
  ...hugsItsLabel,
}));

export const ProposalLoaderContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  height: "100%",
  justifyContent: "center",
  minWidth: 0,
  width: "100%",
}));

export const ProposalPageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));

/**
 * The column the page's comments and fee-schedule panels occupy, and the box they are drawn inside.
 *
 * Two jobs, which are the same job. It reserves the panel's width in the page's row so the content
 * lays itself out against what is left rather than against width the panel is about to cover; and it
 * gives the panel something to be positioned against, because Material draws a persistent drawer's
 * paper `fixed` to the window at the window's full height. Fixed to the window, the paper reached
 * past the bottom of the page area and over the application's footer — with comments open on a
 * 1280px display it covered "Contact Us" and took its clicks (#4964).
 *
 * The page area is bounded above by the app bar and below by the footer, so anchoring here also
 * retires the `margin-top: 64px` each of these panels carried to clear the app bar by hand.
 *
 * The selector reaches one level further than the panels' own `& .MuiDrawer-paper` rules so it wins
 * on specificity rather than on whichever stylesheet emotion happened to insert last.
 */
export const ProposalSidePanelColumn = styled("div")(() => ({
  /**
   * Nothing of the panel reaches outside the column the page reserved for it.
   *
   * A persistent drawer does not leave when it closes: it slides its paper out by its own width and
   * hides it. The paper is absolutely positioned here, so on a page that collapses this column to 0 it
   * went on contributing its 320px to the row's scrollable width — and on a phone that is a page which
   * scrolls 320px sideways onto nothing, landing there on load with the proposal pushed off the left
   * edge. Clipping is what the column already implies.
   */
  overflow: "hidden",
  position: "relative",
  "& .MuiDrawer-docked .MuiDrawer-paper": {
    height: "100%",
    marginTop: 0,
    position: "absolute",
    top: 0,
  },
}));

export const ProposalPageHeader = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  minWidth: 0,
  [paneIsCompact(theme)]: {
    gap: theme.spacing(1),
  },
}));

export const ProposalPageTitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "row",
  minWidth: 0,
}));

export const ProposalPageActionButtons = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  gap: theme.spacing(2),
  [paneIsCompact(theme)]: {
    gap: theme.spacing(1),
  },
}));

export const ProposalPageTitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(0, 1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [paneIsCompact(theme)]: {
    fontSize: "1.25rem",
    paddingLeft: theme.spacing(0.5),
  },
}));

export const ProposalPageTitleBreadcrumb = styled("span")(({ theme }) => ({
  color: "gray",
  display: "inline-flex",
  marginRight: theme.spacing(1),
  whiteSpace: "nowrap",
}));

export const ProposalPageBackButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  height: 40,
  width: 40,
  [paneIsCompact(theme)]: {
    marginLeft: 0,
  },
}));

export const ProposalStickyPageHeader = styled("section")(({ theme }) => ({
  // The page's chrome stands further off the pane's edges than the content it frames — see `PaneBand`.
  // This outranks the pane's own gutter rather than adding to it, so the title, the tab strip and the
  // actions row below all measure from one number.
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  position: "sticky",
  top: 0,
  zIndex: 20,
  "&::before": {
    backgroundColor: theme.palette.background.default,
    content: '""',
    height: theme.spacing(1),
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: theme.spacing(-1),
  },
  [paneIsCompact(theme)]: {
    "&::before": {
      height: theme.spacing(1),
      top: theme.spacing(-1),
    },
  },
}));

export const ProposalPageActionsContainer = styled(ActionsContainer)(({ theme }) => ({
  // The rule under the page's actions separates one band of the page from the next, so it runs the
  // pane's full width and meets the borders on either side rather than stopping inside the gutter.
  // It sits inside the header, so the gutter it has to climb out of is the header's wider one.
  ...workPaneFullBleed(theme, "header"),
  borderBottom: `1px solid ${theme.palette.divider}`,
  bottom: "auto",
  boxSizing: "border-box",
  flexShrink: 0,
  paddingBottom: theme.spacing(1.5),
  position: "static",
  zIndex: "auto",
}));

export const ProposalTabsHeader = styled(Header, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{
  compact?: boolean;
}>(({ compact, theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  [paneIsCompact(theme)]: {
    ...(compact && {
      paddingBottom: 0,
    }),
  },
}));

export const ProposalPageMain = styled("main", {
  shouldForwardProp: (prop) => prop !== "dimmed" && prop !== "mobilePadding" && prop !== "open",
})<{
  dimmed?: boolean;
  mobilePadding?: "compact" | "comfortable";
  open?: boolean;
}>(({ dimmed, mobilePadding = "comfortable", theme }) => ({
  /**
   * The pane names itself here, and everything drawn inside it lays out against this element's width
   * rather than the window's — see `work-pane-layout`. Its own rules are the one exception: a
   * container cannot query itself, which is why the horizontal gutter is declared for the pane's
   * children (`workPaneGutters`) and why the vertical padding below still asks the viewport.
   */
  ...workPaneContainment,
  ...workPaneGutters(theme),
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  marginRight: 0,
  minHeight: 0,
  overflowY: "auto",
  paddingTop: theme.spacing(1),
  position: "relative",
  transition: theme.transitions.create(["margin"], {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp,
  }),
  width: "100%",
  "&::after": {
    backgroundColor: alpha(theme.palette.background.default, 0.38),
    content: '""',
    inset: 0,
    opacity: dimmed ? 1 : 0,
    pointerEvents: "none",
    position: "absolute",
    transition: theme.transitions.create("opacity", {
      duration: dimmed ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      easing: dimmed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    }),
    zIndex: theme.zIndex.drawer - 1,
  },
  // Vertical only: the horizontal gutter is `workPaneGutters` above, which answers about the pane
  // at every width including this one. What is left here is the room a touch device wants above and
  // below the page, which is a question about the device and so still a question for the viewport.
  [theme.breakpoints.down("md")]: {
    padding: mobilePadding === "compact" ? 0 : theme.spacing(2, 0),
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginRight: 0,
        transition: theme.transitions.create(["margin"], {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.easeOut,
        }),
      },
    },
  ],
}));

export const ProposalReviewerActionFieldset = styled(Fieldset)(() => ({
  gridColumnEnd: "auto",
  gridTemplateColumns: "minmax(0, 1fr)",
}));

/**
 * What a proposal pane says when it has nothing to list.
 *
 * The Comments/Changes stream with no entries yet and the archive's comment list with no comments are
 * the same kind of empty — one pane, nothing in it — and they used to be two tabs of one pane. Drawn
 * separately they drifted: the comments side was heading-sized and centered while the changes side was
 * a line of `body2` body copy, so the same "nothing here" arrived as two different kinds of message
 * (#4536). One component so they cannot part again.
 *
 * A pane is the surface this is sized for. A panel embedded beside a single field — the field-history
 * popper, a 300px card — says its "nothing here" in body copy instead; see ProposalTimelinePanel's
 * `surface` prop.
 */
export const ProposalEmptyPaneMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  // The size the empty comment list has always been drawn at, and the treatment the app's empty tables
  // already use (see permissions' NoRowsText): larger than body copy, centered, given its own space.
  fontSize: "1.2rem",
  paddingTop: theme.spacing(2),
  textAlign: "center",
  // The comment list draws this inside a flex `ListItem`, where a content-width child cannot center.
  width: "100%",
}));
